import React, { useState } from "react";
import { IPersonBadge, Nullable } from "../../../../../../common/types";
import { useGenerateOdinBadge } from "../../../../../search/details/download/hooks/useGenerateBadgeOdin/useGenerateOdinBadge";
import { useDispatch, useSelector } from "react-redux";
import { offlineSelector } from "../../../../../../redux/metadata/selectors/metadata.selectors";
import PublicApiService from "../../../../../../services/PublicApiService";
import { useSnackbar } from "notistack";
import { SNACKBAR_VARIANTS } from "../../../../../../common/constants";
import { useTranslation } from "react-i18next";
import { badgesSelector } from "../../../../../../redux/eventEdition/selectors/eventEdition.selectors";
import { BadgeStatuses } from "../../../../../../common/enums";
import PromiseUtils from "../../../../../../common/utils/promiseUtils";
import { setEventEditionBadges } from "../../../../../../redux/eventEdition/slice/eventEditionSlice";
import BadgeImageHelper from "../../../../../../common/helpers/badgeImage/BadgeImageHelper";

export const useCompanyBadgeActions = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("common");
  const offline = useSelector(offlineSelector);
  const badges: IPersonBadge[] = useSelector(badgesSelector);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const { onDownloadClick } = useGenerateOdinBadge({} as IPersonBadge);
  const [activeBadge, setActiveBadge] = useState<Nullable<IPersonBadge>>(null);

  const printBadges = async (badgesToPrint: IPersonBadge[]) => {
    const shouldPrintLogo = BadgeImageHelper.shouldPrintBadgeLogo();

    for (const badge of badgesToPrint) {
      setActiveBadge(badge);
      await PromiseUtils.wait(2500);

      await onDownloadClick(false, { providedBadge: badge, hideToastMessage: true, hideLogo: !shouldPrintLogo });

      await PromiseUtils.wait(2500);
    }
    showPrintBadgesFeedback(badgesToPrint);
  };

  const showPrintBadgesFeedback = (badgesPrinted: IPersonBadge[]) => {
    const message =
      badgesPrinted.length === 1
        ? t("companyPage.feedback.singleBadgePrint")
        : t("companyPage.feedback.multipleBadgePrint", { badgeCount: badgesPrinted.length });
    enqueueSnackbar(<b>{message}</b>, SNACKBAR_VARIANTS.success);
  };

  const showResendEmailsFeedback = (resentBadges: IPersonBadge[], error?: Error) => {
    if (error) {
      enqueueSnackbar(<b>{t("errors.genericClientError")}</b>, SNACKBAR_VARIANTS.error);
      return;
    }
    const message =
      resentBadges.length === 1
        ? t("companyPage.feedback.singleEmailResend")
        : t("companyPage.feedback.multipleEmailResend", { badgeCount: resentBadges.length });
    enqueueSnackbar(<b>{message}</b>, SNACKBAR_VARIANTS.info);
  };

  const resendEmails = async (badgesToResend: IPersonBadge[]) => {
    let error;
    setLoading(true);
    for (const badge of badgesToResend) {
      try {
        await PublicApiService.resendEmail(badge.id);
      } catch (e) {
        error = e;
      }
    }
    showResendEmailsFeedback(badgesToResend, error);
    setLoading(false);
  };

  const deactivateBadges = async (badgesToDeactivate: IPersonBadge[], note: string) => {
    setLoading(true);
    let error;
    let modifiedBadges = JSON.parse(JSON.stringify(badges)) as IPersonBadge[];

    for (const badge of badgesToDeactivate) {
      try {
        await PublicApiService.changeBadgeStatus(badge.id, BadgeStatuses.INACTIVE, note);
        modifiedBadges = modifiedBadges.map(bgFromStore => {
          if (bgFromStore.id === badge.id) {
            bgFromStore.status = BadgeStatuses.INACTIVE;
          }
          return bgFromStore;
        });
      } catch (e) {
        error = e;
      }
    }

    showDeactivateActionFeedback(badgesToDeactivate, error);
    dispatch(setEventEditionBadges(modifiedBadges));
    setLoading(false);
  };

  const showDeactivateActionFeedback = (deactivatedBadges: IPersonBadge[], error?: Error) => {
    if (error) {
      enqueueSnackbar(<b>{t("errors.genericClientError")}</b>, SNACKBAR_VARIANTS.error);
      return;
    }
    const message =
      deactivatedBadges.length === 1
        ? t("companyPage.feedback.singleBadgeDeactivated")
        : t("companyPage.feedback.multipleBadgeDeactivated", { badgeCount: deactivatedBadges.length });
    enqueueSnackbar(<b>{message}</b>, SNACKBAR_VARIANTS.warning);
  };

  return { printBadges, resendEmails, deactivateBadges, loading, activeBadge, isOffline: offline };
};
